import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, TrendingUp, AlertCircle, Activity } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { CostTransitionDashboard } from "./CostTransitionDashboard";
import { BaselineMetrics } from "./BaselineMetrics";

const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))'];

export function CostAnalytics() {
  // Fetch today's cost analytics
  const { data: todayAnalytics, isLoading: loadingToday } = useQuery({
    queryKey: ['cost-analytics-today'],
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('cost_analytics_daily')
        .select('*')
        .eq('date', today);
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch last 30 days analytics
  const { data: historicalAnalytics, isLoading: loadingHistory } = useQuery({
    queryKey: ['cost-analytics-history'],
    queryFn: async () => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data, error } = await supabase
        .from('cost_analytics_daily')
        .select('*')
        .gte('date', thirtyDaysAgo.toISOString().split('T')[0])
        .order('date', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch active alerts
  const { data: alerts } = useQuery({
    queryKey: ['cost-alerts-active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cost_alerts')
        .select('*')
        .eq('is_active', true)
        .eq('status', 'triggered');
      
      if (error) throw error;
      return data;
    },
  });

  // Calculate totals
  const todayTotals = todayAnalytics?.reduce(
    (acc, curr) => ({
      totalCost: acc.totalCost + Number(curr.total_cost),
      totalRevenue: acc.totalRevenue + Number(curr.revenue),
      totalProfit: acc.totalProfit + Number(curr.profit),
    }),
    { totalCost: 0, totalRevenue: 0, totalProfit: 0 }
  ) || { totalCost: 0, totalRevenue: 0, totalProfit: 0 };

  const avgProfitMargin = todayAnalytics?.length 
    ? todayAnalytics.reduce((sum, curr) => sum + Number(curr.profit_margin), 0) / todayAnalytics.length
    : 0;

  // Prepare cost breakdown data
  const costBreakdown = todayAnalytics?.reduce(
    (acc, curr) => ({
      cloud: acc.cloud + Number(curr.cloud_cost),
      ai: acc.ai + Number(curr.ai_cost),
      overage: acc.overage + Number(curr.overage_cost),
      addon: acc.addon + Number(curr.addon_cost),
    }),
    { cloud: 0, ai: 0, overage: 0, addon: 0 }
  );

  const pieData = costBreakdown ? [
    { name: 'Cloud Infrastructure', value: costBreakdown.cloud },
    { name: 'AI Processing', value: costBreakdown.ai },
    { name: 'Overage Charges', value: costBreakdown.overage },
    { name: 'Add-ons', value: costBreakdown.addon },
  ].filter(item => item.value > 0) : [];

  // Prepare trend data
  const trendData = historicalAnalytics?.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    cost: Number(item.total_cost),
    revenue: Number(item.revenue),
    profit: Number(item.profit),
  })) || [];

  if (loadingToday || loadingHistory) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Check if we're in zero-cost mode
  const isZeroCostMode = !todayAnalytics || todayAnalytics.length === 0 || 
    (todayAnalytics.length === 1 && todayAnalytics[0].is_baseline);

  return (
    <div className="space-y-6">
      {/* Zero Cost Mode Display */}
      {isZeroCostMode && (
        <div className="space-y-4">
          <BaselineMetrics />
          <CostTransitionDashboard />
        </div>
      )}

      {/* Alert Banner */}
      {alerts && alerts.length > 0 && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <span className="font-semibold">{alerts.length} Active Cost Alert{alerts.length > 1 ? 's' : ''}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Costs (Today)</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${todayTotals.totalCost.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">Across all subscriptions</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Revenue (Today)</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${todayTotals.totalRevenue.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">From subscriptions & overages</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Profit (Today)</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${todayTotals.totalProfit.toFixed(2)}</div>
            <p className="text-xs text-muted-foreground">Net profit after costs</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Profit Margin</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgProfitMargin.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">Across active subscriptions</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Cost Breakdown Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Cost Breakdown (Today)</CardTitle>
            <CardDescription>Distribution of costs by type</CardDescription>
          </CardHeader>
          <CardContent>
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="hsl(var(--primary))"
                    dataKey="value"
                  >
                    {pieData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => `$${value.toFixed(2)}`} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[300px] items-center justify-center text-muted-foreground">
                No cost data available for today
              </div>
            )}
          </CardContent>
        </Card>

        {/* 30-Day Trend */}
        <Card>
          <CardHeader>
            <CardTitle>30-Day Trend</CardTitle>
            <CardDescription>Cost, revenue, and profit over time</CardDescription>
          </CardHeader>
          <CardContent>
            {trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value: number) => `$${value.toFixed(2)}`} />
                  <Line type="monotone" dataKey="cost" stroke="hsl(var(--destructive))" name="Cost" />
                  <Line type="monotone" dataKey="revenue" stroke="hsl(var(--primary))" name="Revenue" />
                  <Line type="monotone" dataKey="profit" stroke="hsl(var(--success))" name="Profit" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-[300px] items-center justify-center text-muted-foreground">
                No historical data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
